import { Fragment, useEffect, useRef } from 'react';
import clock from '~/assets/icons/clock.svg';
import paper from '~/assets/icons/hand_2.svg';
import scissors from '~/assets/icons/peace.svg';
import rock from '~/assets/icons/stone.svg';
import { PanelControls } from '~/components/ui/PanelControls';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { formatTime, InputAction, RockPaperScissorsChoice } from '~/shared';
import {
	setPlayerChoice,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Key } from '../Key';
import { TextStroke } from '../TextStroke';
import {
	StyledChooseContainer,
	StyledChooseItem,
	StyledControlsContainer,
	StyledGameContainer,
	StyledItemShadow,
	StyledKeyContainer,
	StyledNameContainer,
	StyledTimerContainer,
	StyledTopContainer,
} from './Game.styled';

const Game = () => {
	const dispatch = useLayoutDispatch();
	const opponentName = useObservable(rage.rockPaperScissors.getOpponentName());
	const playerName = useObservable(rage.player.getNickname());
	const playerChoice = useLayoutSelector(
		(state) => state.rockPaperScissors.playerChoice,
	);
	const timerMs = useObservable(rage.rockPaperScissors.getTimer());
	const timerRef = useRef<HTMLSpanElement | null>(null);

	useEffect(() => {
		if (!timerMs || timerMs <= 0) {
			if (timerRef.current) timerRef.current.textContent = '0';
			return;
		}

		const endAt = Date.now() + timerMs;
		let stopped = false;

		const tick = () => {
			if (stopped) return;
			const remaining = Math.max(0, endAt - Date.now());
			if (timerRef.current)
				timerRef.current.textContent = formatTime(remaining, '{ss}');
			if (remaining <= 0) {
				stopped = true;
				rage.rockPaperScissors
					.exit()
					.catch((err) => rage.notifications.showException(err));
			}
		};

		tick();
		const intervalId = setInterval(tick, 200);

		return () => {
			stopped = true;
			clearInterval(intervalId);
		};
	}, [timerMs]);

	const handleSelectItem = (item: RockPaperScissorsChoice) => {
		dispatch(setPlayerChoice(item));
		rage.rockPaperScissors
			.makeChoice(item)
			.catch((err) => rage.notifications.showException(err));
	};

	const controls = [
		{
			text: 'Завершить игру',
			keys: ['Esc'],
			onClick: () =>
				rage.rockPaperScissors
					.exit()
					.catch((err) => rage.notifications.showException(err)),
		},
	];

	useInputAction(InputAction.RockPaperScissorsSelectRock, () =>
		handleSelectItem(RockPaperScissorsChoice.ROCK),
	);
	useInputAction(InputAction.RockPaperScissorsSelectPaper, () =>
		handleSelectItem(RockPaperScissorsChoice.PAPER),
	);
	useInputAction(InputAction.RockPaperScissorsSelectScissors, () =>
		handleSelectItem(RockPaperScissorsChoice.SCISSORS),
	);
	useInputAction(InputAction.CommonMenuClose, () =>
		rage.rockPaperScissors.exit(),
	);

	return (
		<Fragment>
			<StyledGameContainer>
				<StyledTopContainer>
					<StyledNameContainer $direction="left">
						<Text>{playerName}</Text>
						<div className="bg"></div>
					</StyledNameContainer>
					<StyledTimerContainer>
						<img src={clock} />
						<Text>
							<span
								ref={timerRef}
								style={{ display: 'inline-block', width: 18 }}
							></span>{' '}
							сек.
						</Text>
					</StyledTimerContainer>
					<StyledNameContainer $direction="right">
						<Text>{opponentName}</Text>
						<div className="bg"></div>
					</StyledNameContainer>
				</StyledTopContainer>
				<StyledChooseContainer>
					<StyledChooseItem
						$active={playerChoice === RockPaperScissorsChoice.ROCK}
					>
						<StyledItemShadow />
						<img src={rock} className="icon" />
						<Text>Камень</Text>
						<StyledKeyContainer>
							<div className="dot" />
							<div className="dot" />
							<Key
								name="A"
								active={playerChoice === RockPaperScissorsChoice.ROCK}
							/>
							<div className="dot" />
							<div className="dot" />
						</StyledKeyContainer>
					</StyledChooseItem>
					<StyledChooseItem
						$active={playerChoice === RockPaperScissorsChoice.SCISSORS}
					>
						<StyledItemShadow />
						<img src={scissors} className="icon" />
						<Text>Ножницы</Text>
						<StyledKeyContainer>
							<div className="dot" />
							<div className="dot" />
							<Key
								name="S"
								active={playerChoice === RockPaperScissorsChoice.SCISSORS}
							/>
							<div className="dot" />
							<div className="dot" />
						</StyledKeyContainer>
					</StyledChooseItem>
					<StyledChooseItem
						$active={playerChoice === RockPaperScissorsChoice.PAPER}
					>
						<StyledItemShadow />
						<img src={paper} className="icon" />
						<Text>Бумага</Text>
						<StyledKeyContainer>
							<div className="dot" />
							<div className="dot" />
							<Key
								name="D"
								active={playerChoice === RockPaperScissorsChoice.PAPER}
							/>
							<div className="dot" />
							<div className="dot" />
						</StyledKeyContainer>
					</StyledChooseItem>
				</StyledChooseContainer>
				<TextStroke style={{ marginTop: '20px', fontSize: 18 }}>
					Выбери фигуру <span style={{ fontWeight: 500 }}>удерживая</span>{' '}
					клавишу
				</TextStroke>
			</StyledGameContainer>
			<StyledControlsContainer>
				<PanelControls controls={controls} align="right" />
			</StyledControlsContainer>
		</Fragment>
	);
};

export { Game };
